import { useRef, useState, useEffect } from 'react';
import '../../assets/scss/components/common/viewinggallery.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

// CSS Lightbox-style Carousel that currently handles images but could be made to handle other types of data
export default function ViewingGallery({images, imageBaseUrl, isVisible, handleClose})
{
    //#region --- Refs ---
    const viewImageOverlayRef = useRef(null);
    const prevIconContainerRef = useRef(null);
    const nextIconContainerRef = useRef(null);
    const galleryRef = useRef(null);
    const galleryIndicatorsRef = useRef(null);
    //#endregion --- Refs ---

    //#region --- Functions ---
    const getIndexVisible = ()  =>
    {
        if (galleryRef.current)
        {
		    for (let i = 0; i < galleryRef.current.children.length; i++) 
            {
                let rect = galleryRef.current.children[i].getBoundingClientRect();

                if (rect.top >= 0 &&
                    rect.left >= 0 &&
                    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                    rect.right <= (window.innerWidth || document.documentElement.clientWidth))
                {
                    return i;
                }
            }
        }

        return null;
    }

    const setNavState = (index) =>
    {
        // set prev and next arrows
        if (prevIconContainerRef.current && nextIconContainerRef.current && index !== null)
        {
            prevIconContainerRef.current.disabled = false;
            nextIconContainerRef.current.disabled = false;

            if (prevIconContainerRef.current && (index - 1) < 0)
            {
                prevIconContainerRef.current.disabled = true;
            }

            if (nextIconContainerRef.current && galleryRef?.current?.children && (index + 1) >= galleryRef.current.children.length)
            {
                nextIconContainerRef.current.disabled = true;
            }
        }

        // set
        if (galleryIndicatorsRef.current?.children?.length)
        {
            for (let i = 0; i < galleryIndicatorsRef.current.children.length; i++)    
            {
                if (galleryIndicatorsRef.current.children[i].classList.contains("selected"))
                {
                    galleryIndicatorsRef.current.children[i].classList.remove("selected");
                }

                if (i === index)
                {
                    galleryIndicatorsRef.current.children[i].classList.add("selected");
                }
            }
        }
    }

    const setImageLoad = () => 
    {
        // Increment the counter when an image is loaded
        setImagesLoaded(prev => prev + 1);
    };

    //#endregion --- Functions ---

    //#region --- State ---

    const [imageUrlList, setImageUrlList] = useState(images);
    const [imagesLoaded, setImagesLoaded] = useState(0);
    const [galleryIndicatorsStyle, setGalleryIndicatorsStyle] = useState({});

    //#endregion --- State ---

    //#region --- Event Handlers ---

    const handlePrevClick = (e) =>
    {
        e.stopPropagation();
        if (getIndexVisible() === 0)
        {
            // block click
            return;
        }

        if (galleryRef.current)
        {
            const index = getIndexVisible();
            let newIndex = index - 1;

            // avoid errors when spamming
            if (index !== null && newIndex < 0)
            {
                return;
            }
            else if (index !== null)
            {
                galleryRef.current.children[newIndex].scrollIntoView();
                setNavState(newIndex);
            }
        }
    }
    
    const handleNextClick = (e) =>
    {
        // see which image is in view and then calc off that?

        e.stopPropagation();
        if (getIndexVisible() === (imageUrlList.length - 1))
        {
            // block click
            return;
        }

        if (galleryRef.current)
        {
            const index = getIndexVisible();
            let newIndex = index + 1;

            // avoid errors when spamming
            if (index === null || (index !== null && newIndex >= galleryRef.current.children.length))
            {
                return;
            }
            else
            {
                galleryRef.current.children[newIndex].scrollIntoView();
                setNavState(newIndex);
            }
        }
    }

    const handleCloseAttempt = () =>
    {
        setNavState(getIndexVisible());
        handleClose();
    }

    const handlePipClick = (index) =>
    {
        setNavState(index);

        // slide scrollbar to selected slide
        if (galleryIndicatorsRef.current?.children?.length)
        {
            if (galleryRef.current && index !== null)
            {
                galleryRef.current.children[index].scrollIntoView();
            }
        }
    }

    //#endregion --- Event Handlers ---

    //#region --- Effects ---

    // cater for lazy loaded images messing with scrollbar
    useEffect(() => 
    {
        // Chackes all images have loaded, then scrolls fully to left
        const checkImagesLoaded = () => 
        {
            if (imagesLoaded === imageUrlList.length) 
            {
                galleryRef.current.scrollLeft = 0;
            }
        };

        // Reset the counter when the component mounts
        setImagesLoaded(0);

        // Attach onLoad event listeners to each slide image
        if (galleryRef.current && galleryRef.current.children) 
        {
            for (let i = 0; i < galleryRef.current.children.length; i++)    
            {
                let slide = galleryRef.current.children[i]; 

                if (slide.children.length > 0) 
                {
                    const image = slide.children[0];
            
                    if (image && image.tagName === 'IMG') 
                    {
                        image.addEventListener('load', setImageLoad);
                    }
                }
            }
        }

        // Check if all images are loaded after each image load
        checkImagesLoaded();

        // Cleanup: Remove event listeners when the component unmounts
        return () => {
            if (galleryRef.current && galleryRef.current.children) 
            {
                for (let i = 0; i < galleryRef.current.children.length; i++)    
                {
                    let slide = galleryRef.current.children[i]; 

                    if (slide.children.length > 0) 
                    {
                        const image = slide.children[0];
            
                        if (image && image.tagName === 'IMG') 
                        {
                            image.removeEventListener('load', setImageLoad);
                        }
                    }
                }
            }
        };
    }, [imagesLoaded]); // Re-run the effect when imagesLoaded changes

    // set nav state and image list when the gallyer is viewed or images changed
    useEffect(() => 
    {
        if (isVisible)
        {
            setNavState(getIndexVisible());

            if (galleryRef.current)
            {
                setImageUrlList(images);
                galleryRef.current.scrollLeft = 0;
                setNavState(0);
            }

            let galleryIndicatorsStyle = {
                left: "calc(50% - " + (galleryIndicatorsRef.current && galleryIndicatorsRef.current.children ? (galleryIndicatorsRef.current.clientWidth / 2) + "px" : "0px") + ")"
            };

            setGalleryIndicatorsStyle(galleryIndicatorsStyle);
        }
    }, [isVisible]); // Re-run the effect when isVisible changes

    // add an event listener to the gallery element to pick up when scroll events have finished
    useEffect(() => 
    {
        const setScrollState = () => 
        {
            // if the scroll bar isn't still scrolling, set nav
            if (galleryRef.current.scrollLeft === 0 || (Math.round(galleryRef.current.scrollLeft) % galleryRef.current.clientWidth === 0))
            {
                handlePipClick(getIndexVisible());
            }
            else
            {
                // wait a bit longer
                setTimeout(function() {
                    if (galleryRef.current.scrollLeft === 0 || (Math.round(galleryRef.current.scrollLeft) % galleryRef.current.clientWidth === 0))
                    {
                        handlePipClick(getIndexVisible());
                    }
                }, 1250);
            }
        };

        if (galleryRef.current && galleryRef.current.children) 
        {
            galleryRef.current.addEventListener('scrollend', setScrollState);
        }

        if (galleryRef.current && galleryRef.current.children) 
        {
            galleryRef.current.addEventListener('scrollstart', setScrollState);
        }

        // Cleanup: Remove event listeners when the component unmounts
        return () => {
            if (galleryRef.current && galleryRef.current.children) 
            {
                galleryRef.current.removeEventListener('scrollend', setScrollState);
            }

            if (galleryRef.current && galleryRef.current.children) 
            {
                galleryRef.current.removeEventListener('scrollstart', setScrollState);
            }
        };

    }, []); 
    
    //#endregion --- Effects ---

    //#region --- Component Logic ---

    if (imageUrlList[0] !== images[0])
    {
        setImageUrlList(images);
    }

    //#endregion --- Component Logic ---

    return (
        <div className={"viewing-gallery-wrapper" + (!isVisible ? " d-none" : "")}>
        
            <div ref={viewImageOverlayRef} className={"d-flex align-items-center justify-content-center view-image-overlay offcanvas-backdrop fade" + (isVisible ? " show" : "")} onClick={handleCloseAttempt}>

                <div className="gallery-grid-container position-relative">
                    <button ref={prevIconContainerRef} className={"prev-icon-container justify-content-center align-items-center position-absolute border-0 d-flex" + ((imageUrlList.length <= 1 || imageUrlList[0] !== images[0]) ? " d-none" : "")} aria-hidden="true" onClick={handlePrevClick} type="button">
                        <FontAwesomeIcon icon={icon({ name: 'chevron-left', style: 'solid' })} className="prev-icon position-relative"/>
                    </button>
                    <div ref={galleryRef} className="gallery" onClick={ (e) => e.stopPropagation() } >
                        {
                            imageUrlList[0] === images[0]
                            &&
                                imageUrlList.map((imageUrl, index) =>
                                {
                                    return (
                                        <div key={"image_" + index} className="slide">
                                            <img src={imageBaseUrl + imageUrl + "?scale=both"} className="w-100 px-2" alt={"Image " + (index + 1)} />
                                        </div>
                                    )
                                })
                        }
                    </div>
                    <button ref={nextIconContainerRef} className={"next-icon-container justify-content-center align-items-center position-absolute border-0 d-flex" + ((imageUrlList.length <= 1 || imageUrlList[0] !== images[0]) ? " d-none" : "")} aria-hidden="true" onClick={handleNextClick} type="button">
                        <FontAwesomeIcon icon={icon({ name: 'chevron-right', style: 'solid' })} className={"next-icon position-relative"}/>
                    </button>
                    <button className={"close-icon-container d-flex justify-content-center align-items-center position-absolute border-0" + (imageUrlList[0] !== images[0] ? " d-none" : "")} aria-hidden="true" onClick={handleCloseAttempt} type="button">
                        <FontAwesomeIcon icon={icon({ name: 'times', style: 'solid' })} className="close-icon position-relative" />
                    </button>

                    <div ref={galleryIndicatorsRef} className={"gallery-indicators-container position-absolute d-flex justify-content-center align-items-center" + (imageUrlList[0] !== images[0] ? " d-none" : "")} style={galleryIndicatorsStyle} onClick={ (e) => e.stopPropagation() }>
                        {
                            imageUrlList.map((imageUrl, index) =>
                            {
                                return (
                                    <button key={"imageIndicator_" + index} className={"indicator position-relative p-0" + (index === 0 ? " selected" : "")} onClick={(e) => { e.stopPropagation(); handlePipClick(index) }} type="button" />
                                )
                            })
                        }
                    </div>
                </div>

            </div>
            
        </div>
    );
}